import * as Sentry from '@sentry/react';
import {config} from './config';
import type {AuthUser} from './security/auth';
import {CaptureContext} from '@sentry/types';

Sentry.init({
    enabled: !!config.sentryDsn,
    dsn: config.sentryDsn,
    environment: config.sentryEnvironment,
    release: config.sentryRelease,
    ignoreErrors: [
        /Loading (CSS )?chunk \d+ failed/i,
        // Assets of a previous deployment asked for by a tab that predates it: lazyWithRetry
        // reloads the page, what is left is the tab that could not.
        /Failed to fetch dynamically imported module/i,
        /Importing a module script failed/i,
        /error loading dynamically imported module/i,
        /Unable to preload CSS/i,
        /^Network Error$/i,
        /^Failed to fetch$/i,
        /^NetworkError when attempting to fetch resource/i,
        /^Load failed$/i,
        /^Request failed with status code/i,
        /^Request aborted$/i,
        /^timeout exceeded$/i,
        /^timeout of \d+ms exceeded$/i,
        /^Non-Error promise rejection captured with value: Object Not Found/i,
        /zaloJSV2/i,
        /property 'javaEnabled' is a read-only/i,
        /**
         * React internal error thrown when something outside react modifies the DOM
         * This is usually because of a browser extension or Chrome's built-in translate
         */
        /NotFoundError: Failed to execute 'removeChild' on 'Node'/,
        /NotFoundError: Failed to execute 'insertBefore' on 'Node'/,
        // In-app browsers (Instagram, Facebook, LinkedIn, DuckDuckGo…) talking to their
        // native side through bridges that fail on their own
        /window\.webkit\.messageHandlers/i,
        /Java object is gone/i,
        /Java exception was raised during method invocation/i,
        /WKWebView API client did not respond/i,
        /Script message handler is unavailable/i,
        /^454: Handling is disabled/i,
        /__firefox__/,
        /Can't find variable: EmptyRanges/i,
        // Browser extensions
        /runtime\.sendMessage\(\)/i,
        /browser\.storage\.local\.set\(\)/i,
        /Attempted to register a sync event/i,
        // A script patching the DOM that serializes the nodes React hands it
        /Converting circular structure to JSON[\s\S]*__reactFiber/,
        // A script added to the page by another one (a tag, an extension) failed to load and
        // that one rejected with the load event: scriptLoader rejects with an Error
        /^Event `Event` \(type=error\) captured as promise rejection/,
        // Another Sentry SDK on the page (in-app browser instrumentation) reporting its own
        // dropped events through the console
        /An event processor returned `null`/i,
    ],
    denyUrls: [
        /webkit-masked-url/i,
        /safari-web-extension/i,
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        // Android in-app browsers
        /^iabjs:\/\//i,
        // Facebook flakiness
        /graph\.facebook\.com/i,
        // Tracking pixels
        /ct\.pinterest\.com/i,
    ],
    beforeSend: event => (isInjectedScriptError(event) ? null : event),
    tracesSampler: samplingContext => {
        if (samplingContext.location?.host.startsWith('profile')) {
            return 0.2;
        }

        return 0.01;
    },
});

/**
 * Chrome for iOS, Android WebViews and translation features run their own scripts in the
 * page, reported under the document URL or no file at all. An error whose every frame
 * sits outside our bundles is theirs.
 *
 * Errors with no frame at all (a rejected DOM promise, say) are kept: nothing tells.
 */
function isInjectedScriptError(event: Sentry.ErrorEvent): boolean {
    const frames = (event.exception?.values ?? []).flatMap(
        value => value.stacktrace?.frames ?? [],
    );
    if (frames.length === 0) {
        return false;
    }

    return !frames.some(frame => /\/assets\//.test(frame.filename ?? ''));
}

export function logError(error: any, captureContext?: CaptureContext): void {
    // eslint-disable-next-line no-console
    console.error(error);
    Sentry.captureException(error, captureContext);
}

export function setSentryUser(user: AuthUser | undefined) {
    Sentry.setUser(
        user
            ? {
                  id: user.id,
                  email: user.email,
              }
            : null,
    );
}
